// crewbit-admin/src/components/schedule/MonthCanvas.tsx
//
// Schedule Module Phase C C3 — Month-view top-level orchestrator (admin).
// Composes <MonthHeader> + <ConflictBanner> slot + <MonthGrid> with HTML5
// cross-cell drag-drop for date-change reschedule per spec §4.8.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §4 + §C.6.

import { useMemo } from 'react';
import { admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import type { ConflictDetail } from '../../lib/errorMap';
import type { LeaseBoundaryKind } from '../../hooks/useLeaseBoundariesByMonth';
import { ConflictBanner } from './ConflictBanner';
import { MonthGrid } from './MonthGrid';
import { MonthHeader } from './MonthHeader';

export interface MonthCanvasProps {
  date: string;
  entries: ScheduleEntry[];
  leaseBoundariesByDate?: Map<string, LeaseBoundaryKind>;
  showRecurringBadges?: boolean;
  onCellTap: (date: string) => void;
  onCellDrop?: (date: string, droppedEntryId: string) => void;
  conflicts?: ConflictDetail[] | null;
  conflictRpcName?: string;
  onConflictOverride?: () => Promise<void>;
  onConflictDismiss?: () => void;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  onToday?: () => void;
}

function startOfMonth(iso: string): Date {
  const d = new Date(`${iso}T12:00:00`);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfMonthGridSunday(iso: string): Date {
  const monthStart = startOfMonth(iso);
  const dow = monthStart.getDay();
  const gridStart = new Date(monthStart);
  gridStart.setDate(gridStart.getDate() - dow);
  return gridStart;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function entryIsoDate(entry: ScheduleEntry): string {
  return entry.start_at.slice(0, 10);
}

export function MonthCanvas({
  date,
  entries,
  leaseBoundariesByDate,
  showRecurringBadges,
  onCellTap,
  onCellDrop,
  conflicts,
  conflictRpcName,
  onConflictOverride,
  onConflictDismiss,
  onPrevMonth,
  onNextMonth,
  onToday,
}: MonthCanvasProps) {
  const monthStart = useMemo(() => startOfMonth(date), [date]);
  const monthIndex = monthStart.getMonth();
  const days = useMemo(() => {
    const gridStart = startOfMonthGridSunday(date);
    const out: { iso: string; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      out.push({ iso: toIso(d), isCurrentMonth: d.getMonth() === monthIndex });
    }
    return out;
  }, [date, monthIndex]);

  const entriesByDate = useMemo(() => {
    const grouped: Record<string, ScheduleEntry[]> = {};
    for (const day of days) grouped[day.iso] = [];
    for (const e of entries) {
      const k = entryIsoDate(e);
      if (grouped[k]) grouped[k].push(e);
    }
    return grouped;
  }, [entries, days]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      flex: 1, height: '100%',
      backgroundColor: admin.bg,
    }}>
      <MonthHeader monthStart={monthStart} onPrev={onPrevMonth} onNext={onNextMonth} onToday={onToday} />

      {conflicts && conflicts.length > 0 && onConflictOverride && onConflictDismiss && conflictRpcName ? (
        <ConflictBanner
          conflicts={conflicts}
          pendingMutationContext={{
            rpcName: conflictRpcName,
            onOverride: onConflictOverride,
          }}
          onDismiss={onConflictDismiss}
        />
      ) : null}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <MonthGrid
          days={days}
          entriesByDate={entriesByDate}
          leaseBoundariesByDate={leaseBoundariesByDate}
          showRecurringBadges={showRecurringBadges}
          onCellTap={onCellTap}
          onCellDrop={onCellDrop}
        />
      </div>
    </div>
  );
}
